import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { generateFilterStateKey } from '@atlassian/notification-common';
import { LoadingState, MarkRequestReadState, RequestCategory } from '@atlassian/notification-common/types';
import resolveLoadingStates from '../../../use-notification-list-filters-store/utils/resolve-loading-states';
import { DEFAULT_FILTER_STATE, getFilterStateOrDefaultByKey } from '../../../utils/build-filter-state';
import { markAllApiRoute, markAllNotificationsReadFetch } from '../../../utils/mark-all-client';
import { markBulkApiRoute, markBulkNotificationsFetch } from '../../../utils/mark-bulk-client';
import { requestReadStateToRender } from '../../utils/mark-bulk-notifications';
import { updateNotificationDocuments } from '../../utils/merge-paginated-notifications';
import selectFilteredNotifications from '../selectors';
import { markAllGroupsAsRead } from './mark-all-groups-as-read';
import { markGroup } from './mark-group';
import { markGroupNotifications } from './mark-group-notification';
import { resetNotificationListStore } from './reset-notification-list-store';
import { updateGroup } from './update-group';
import { updateGroups } from './update-groups';
var markNotifications = function markNotifications(notifications, toState) {
  return function (_ref) {
    var setState = _ref.setState,
      getState = _ref.getState;
    var previousState = getState();
    var normalizedNotifications = notifications.reduce(function (acc, notification) {
      var existingNotification = previousState.notifications[notification.id];
      if (existingNotification) {
        acc[notification.id] = _objectSpread(_objectSpread({}, existingNotification), {}, {
          readState: requestReadStateToRender(toState)
        });
      }
      return acc;
    }, _objectSpread({}, previousState.notifications));
    setState({
      notifications: normalizedNotifications
    });
  };
};
var markBulkNotifications = function markBulkNotifications(notifications, toState, handlers) {
  return function (_ref2) {
    var dispatch = _ref2.dispatch;
    markBulkNotificationsFetch(notifications.map(function (notif) {
      return notif.id;
    }), toState).then(function (_) {
      return handlers.onSuccess(markBulkApiRoute);
    }).catch(function (e) {
      return handlers.onError(markBulkApiRoute, e.code);
    });
    dispatch(markNotifications(notifications, toState));
  };
};
var setNotifications = function setNotifications(payload) {
  return function (_ref3) {
    var setState = _ref3.setState,
      getState = _ref3.getState;
    if (payload.length === 0) {
      return;
    }
    var previousState = getState();
    var notifications = previousState.notifications;
    var updatedIds = _toConsumableArray(previousState.ids);
    var normalizedNotifications = _objectSpread({}, notifications);
    payload.forEach(function (notification) {
      var existingNotification = notifications[notification.id];

      // Don't insert duplicate ids
      if (existingNotification) {
        var _updateNotificationDo;
        normalizedNotifications[notification.id] = (_updateNotificationDo = updateNotificationDocuments(existingNotification, notification)) !== null && _updateNotificationDo !== void 0 ? _updateNotificationDo : existingNotification;
      } else {
        updatedIds.push(notification.id);
        normalizedNotifications[notification.id] = notification;
      }
    });
    setState({
      ids: updatedIds.sort(function (a, b) {
        // Keep notification with identical timestamps in order that is returned by the API
        // Otherwise, identical timestamp could mess up with tracking 'lastNotificationId'
        if (normalizedNotifications[a].timestamp === normalizedNotifications[b].timestamp) {
          return 0;
        }
        return normalizedNotifications[a].timestamp < normalizedNotifications[b].timestamp ? 1 : -1;
      }),
      notifications: normalizedNotifications
    });
  };
};
var updateFilterState = function updateFilterState(newFilters) {
  return function (_ref4) {
    var getState = _ref4.getState,
      setState = _ref4.setState;
    // Reset the state to the default state each time. This will cause notifications to refetch rather than persisting.
    var filterStateKey = generateFilterStateKey(newFilters);
    setState({
      filterStates: _objectSpread(_objectSpread({}, getState().filterStates), {}, _defineProperty({}, filterStateKey, _objectSpread({}, DEFAULT_FILTER_STATE))),
      groups: []
    });
  };
};

/**
 * Action looks at all the current notifications in the store and updates the `isUnseen` property to false.
 */
var markAllAsSeen = function markAllAsSeen() {
  return function (_ref5) {
    var getState = _ref5.getState,
      setState = _ref5.setState;
    var previousState = getState();
    var normalisedNotifications = previousState.ids.reduce(function (acc, notificationId) {
      var existingNotification = previousState.notifications[notificationId];
      if (existingNotification && existingNotification.isUnseen) {
        acc[notificationId] = _objectSpread(_objectSpread({}, existingNotification), {}, {
          isUnseen: false
        });
      }
      return acc;
    }, _objectSpread({}, previousState.notifications));
    setState({
      notifications: normalisedNotifications
    });
  };
};
var actions = {
  resetNotificationListStore: resetNotificationListStore,
  markBulkNotifications: markBulkNotifications,
  updateFilterState: updateFilterState,
  markAllAsSeen: markAllAsSeen,
  markAllNotificationsRead: function markAllNotificationsRead(handlers, filters) {
    return function (_ref6) {
      var dispatch = _ref6.dispatch,
        getState = _ref6.getState;
      var notificationsToMark = selectFilteredNotifications(getState(), filters);
      if (notificationsToMark.length > 0) {
        markAllNotificationsReadFetch(filters.categoryFilter === RequestCategory.DIRECT, notificationsToMark[0].id, filters).then(function (_) {
          return handlers.onSuccess(markAllApiRoute);
        }).catch(function (e) {
          return handlers.onError(markAllApiRoute, e.code);
        });
        dispatch(markBulkNotifications(notificationsToMark, MarkRequestReadState.READ, handlers));
      }
    };
  },
  setNotifications: setNotifications,
  updateGroups: updateGroups,
  updateGroup: updateGroup,
  markGroup: markGroup,
  markAllGroupsAsRead: markAllGroupsAsRead,
  markGroupNotifications: markGroupNotifications,
  disableListFetching: function disableListFetching(filters) {
    return function (_ref7) {
      var setState = _ref7.setState,
        getState = _ref7.getState;
      var previousState = getState();
      var filterStateKey = generateFilterStateKey(filters);
      var filterState = getFilterStateOrDefaultByKey(previousState.filterStates, filterStateKey);
      setState({
        filterStates: _objectSpread(_objectSpread({}, previousState.filterStates), {}, _defineProperty({}, filterStateKey, _objectSpread(_objectSpread({}, filterState), {}, {
          continuationToken: undefined,
          fetchState: LoadingState.COMPLETE,
          fetchingDisabled: true
        })))
      });
    };
  },
  updateNotifications: function updateNotifications(payload) {
    return function (_ref8) {
      var dispatch = _ref8.dispatch,
        getState = _ref8.getState,
        setState = _ref8.setState;
      var filterLocalState = getState();
      var filterStateKey = generateFilterStateKey(payload.filters);
      var filterState = getFilterStateOrDefaultByKey(filterLocalState.filterStates, filterStateKey);
      if (filterState.fetchingDisabled) {
        return;
      }
      var fetchState = resolveLoadingStates(payload.loadingStates, filterState.fetchState);

      // We don't want to rely on the continuation token for this value as it could change in the future to not be notification.id
      // and we don't ever want to clear out the last notification ID otherwise
      // we could lose the position and display notifications further ahead on a filter change.
      // It's also useful to track if a list has been loaded at least once since it won't clear out
      // like the LoadingStates or the continuationToken
      var lastNotificationId = filterState.lastNotificationId;
      if (payload.notifications.length > 0) {
        lastNotificationId = payload.notifications[payload.notifications.length - 1].id;
        dispatch(setNotifications(payload.notifications));
      }
      setState({
        filterStates: _objectSpread(_objectSpread({}, filterLocalState.filterStates), {}, _defineProperty({}, filterStateKey, _objectSpread(_objectSpread({}, filterState), {}, {
          fetchState: fetchState,
          lastNotificationId: lastNotificationId,
          continuationToken: payload.continuationToken
        })))
      });
    };
  }
};
export default actions;